/**
 * 提示词模板渲染器。
 *
 * 模板正文存在 `prompt-templates.json`（唯一真源，可直接改，改完刷新即生效），
 * 这里只做三件事：查模板 → 求值条件块 → 替换变量。
 *
 * 语法刻意做得极简，因为模板是给人改的：
 *   {{变量}}                      —— 替换
 *   {{#if flag}}...{{/if}}        —— 条件为真时保留
 *   {{#if !flag}}...{{/if}}       —— 条件为假时保留
 * 不支持嵌套、不支持表达式。模板一旦需要写逻辑，就该拆成两个模板。
 */
import raw from './prompt-templates.json'

export type PromptTemplate = {
  title: string
  group: string
  body: string
}

export type TemplateVars = Record<string, string | number>
export type TemplateFlags = Record<string, boolean>

const TEMPLATES = (raw as { templates: Record<string, PromptTemplate> }).templates
export const TEMPLATE_VERSION = (raw as { version: string }).version
export const TEMPLATE_NOTES = (raw as { notes: string[] }).notes

export function getTemplate(id: string): PromptTemplate | undefined {
  return TEMPLATES[id]
}

export function allTemplateIds(): string[] {
  return Object.keys(TEMPLATES)
}

const IF_BLOCK = /\{\{#if\s+(!?)(\w+)\s*\}\}([\s\S]*?)\{\{\/if\}\}/g
const VAR = /\{\{(\w+)\}\}/g

export function renderTemplate(templateId: string, vars: TemplateVars, flags: TemplateFlags): string {
  const tpl = getTemplate(templateId)
  if (!tpl) return `（缺少模板 ${templateId}）`

  let out = tpl.body.replace(IF_BLOCK, (_m, negate: string, key: string, inner: string) => {
    const on = Boolean(flags[key])
    return (negate ? !on : on) ? inner : ''
  })

  out = out.replace(VAR, (_m, key: string) => {
    const v = vars[key]
    return v === undefined || v === null ? '' : String(v)
  })

  // 条件块被裁掉后会留下空行，压掉连续空行，避免提示词里出现大段空白
  return out
    .split('\n')
    .filter((line, i, arr) => !(line.trim() === '' && arr[i - 1]?.trim() === ''))
    .join('\n')
    .trim()
}

/** 把整套模板导出成 Markdown，供人工评审与交付。 */
export function exportTemplatesMarkdown(): string {
  const groups = new Map<string, string[]>()
  for (const [id, t] of Object.entries(TEMPLATES)) {
    const list = groups.get(t.group) ?? []
    list.push(id)
    groups.set(t.group, list)
  }

  const lines: string[] = [
    '# 电商工作台 · 提示词模板',
    '',
    `> 版本 ${TEMPLATE_VERSION} · 更新于 ${(raw as { updatedAt: string }).updatedAt}`,
    '> 本文件由 `src/config/prompt-templates.json` 自动生成，改模板请改 JSON。',
    '',
    '## 语法说明',
    '',
    ...TEMPLATE_NOTES.map((n) => `- ${n}`),
    '',
    '---',
    '',
  ]

  for (const [group, ids] of groups) {
    lines.push(`## ${group}`, '')
    for (const id of ids) {
      const t = TEMPLATES[id]
      lines.push(`### ${t.title}`, '', `\`${id}\``, '', '```text', t.body, '```', '')
    }
  }

  return lines.join('\n')
}
